import Modal from "@/components/Modal";
import SkeletonDashboard from "@/components/SkeletonDashboard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card"; // Import Card
import useApiBaseUrl from "@/hooks/useApiBaseUrl";
import { useTahun } from "@/hooks/useTahun";
import { getDataRekapPendaftaranKembali } from "@/services/getData";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  FaClipboardList,
  FaMoneyCheckAlt,
  FaUserCheck,
  FaUserGraduate,
} from "react-icons/fa"; // Import Icons
import { LuDownload, LuListFilter } from "react-icons/lu";
import { useSearchParams } from "react-router-dom";
import FormFilter from "./FormFilter";

type DataRekapPendaftaran = {
  jalur_jenis: string;
  fakultas_nama: string;
  jurusan_nama: string;
  prodi_nama: string;
  jumlah_pendaftar: number;
  jumlah_validasi: string;
  jumlah_lulus: number;
  jumlah_daftar_kembali: number;
  jumlah_isi_ukt: number;
  jumlah_valid_isi_ukt: number;
  jumlah_bayar_ukt: number;
  daya_tampung?: number;
};

// --- HELPER COMPONENT: STAT CARD ---
const StatCard = ({
  title,
  value,
  subValue,
  subLabel,
  colorClass,
  icon,
}: any) => (
  <Card className={`border-none shadow-sm dark:bg-slate-900 ${colorClass}`}>
    <CardContent className="p-4 flex items-center justify-between">
      <div>
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
          {title}
        </p>
        <div className="flex items-baseline gap-2">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            {value}
          </h2>
        </div>
        {subValue !== undefined && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {subLabel}: <span className="font-semibold">{subValue}</span>
          </p>
        )}
      </div>
      <div className="p-3 bg-white/50 dark:bg-slate-800/50 rounded-full text-slate-700 dark:text-slate-300">
        {icon}
      </div>
    </CardContent>
  </Card>
);

// --- KOMPONEN UTAMA ---
export default function TabelRekap() {
  const [searchParams] = useSearchParams();
  const idsUkParams = searchParams.get("ids_uk") || "";
  const idsJalurJenisParams = searchParams.get("ids_ref_jalur_jenis") || "";
  const idsFakultasParams = searchParams.get("ids_fakultas") || "";

  const { apiBaseUrl } = useApiBaseUrl();
  const [showFilter, setShowFilter] = useState(false);
  const { selectedTahun } = useTahun();

  const {
    data: dataRekapPendaftaran = [],
    isPending: isPendingDataRekapPendaftaran,
  } = useQuery({
    queryKey: [
      "dataRekapPendaftaran",
      idsJalurJenisParams,
      idsUkParams,
      selectedTahun,
      idsFakultasParams,
    ],
    queryFn: () =>
      getDataRekapPendaftaranKembali(
        idsJalurJenisParams,
        idsUkParams,
        selectedTahun?.label,
        idsFakultasParams,
      ),
  });

  const isFilterActive = !!idsJalurJenisParams || !!idsUkParams;

  if (isPendingDataRekapPendaftaran) return <SkeletonDashboard />;

  return (
    <div className="space-y-6">
      {/* 1. STATISTIC CARDS SECTION */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Lulus"
          value={dataRekapPendaftaran?.total?.total_lulus || 0}
          subLabel="Daya Tampung"
          subValue={dataRekapPendaftaran?.total?.total_daya_tampung || 0}
          colorClass="bg-blue-50 dark:bg-blue-950/20"
          icon={
            <FaUserGraduate className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          }
        />
        <StatCard
          title="Validasi UKT"
          value={dataRekapPendaftaran?.total?.total_valid_isi_ukt || 0}
          subLabel="Sudah Isi UKT"
          subValue={dataRekapPendaftaran?.total?.total_isi_ukt || 0}
          colorClass="bg-purple-50 dark:bg-purple-950/20"
          icon={
            <FaClipboardList className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          }
        />
        <StatCard
          title="Sudah Bayar UKT"
          value={dataRekapPendaftaran?.total?.total_bayar_ukt || 0}
          colorClass="bg-emerald-50 dark:bg-emerald-950/20"
          icon={
            <FaMoneyCheckAlt className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          }
        />
        <StatCard
          title="Daftar Kembali"
          value={dataRekapPendaftaran?.total?.total_daftar_kembali || 0}
          colorClass="bg-amber-50 dark:bg-amber-950/20"
          icon={
            <FaUserCheck className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          }
        />
      </section>

      {/* 2. HEADER SECTION */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            Rekapitulasi Pendaftaran
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Monitoring data pendaftar, kelulusan, dan daftar kembali.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={isFilterActive ? "default" : "outline"}
            size="sm"
            className={`flex items-center gap-2 transition-all ${
              isFilterActive
                ? ""
                : "border-slate-300 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            }`}
            onClick={() => setShowFilter((show) => !show)}
          >
            <LuListFilter className="w-4 h-4" />
            <span>Filter</span>
            {isFilterActive && (
              <span className="flex h-2 w-2 rounded-full bg-white ml-1 animate-pulse" />
            )}
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2 border-slate-300 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            onClick={() => {
              const exportParams = new URLSearchParams();
              if (idsJalurJenisParams)
                exportParams.set("ids_ref_jalur_jenis", idsJalurJenisParams);
              if (idsUkParams) exportParams.set("ids_uk", idsUkParams);
              if (idsFakultasParams)
                exportParams.set("ids_fakultas", idsFakultasParams);
              if (selectedTahun)
                exportParams.set("tahun", String(selectedTahun.value));

              window.open(
                `${apiBaseUrl}/dashboard/rekap-pendaftaran/export?${exportParams.toString()}`,
              );
            }}
          >
            <LuDownload className="w-4 h-4" />
            <span>Export Excel</span>
          </Button>
        </div>
      </div>

      {/* 3. TABLE SECTION */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-sm overflow-hidden">
        <div className="overflow-x-auto h-[65vh] relative">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-900 dark:text-slate-400 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Jalur
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Fakultas
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Jurusan
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Program Studi
                </th>
                <th className="px-4 py-3 font-semibold text-center whitespace-nowrap">
                  Daya Tampung
                </th>
                <th className="px-4 py-3 font-semibold text-center whitespace-nowrap">
                  Peminat
                </th>
                <th className="px-4 py-3 font-semibold text-center whitespace-nowrap">
                  Lulus
                </th>
                <th className="px-4 py-3 font-semibold text-center whitespace-nowrap">
                  Isi UKT
                </th>
                <th className="px-4 py-3 font-semibold text-center whitespace-nowrap">
                  Valid Isi UKT
                </th>
                <th className="px-4 py-3 font-semibold text-center whitespace-nowrap">
                  Bayar UKT
                </th>
                <th className="px-4 py-3 font-semibold text-center whitespace-nowrap">
                  Daftar Kembali
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {dataRekapPendaftaran?.rekap?.length > 0 ? (
                dataRekapPendaftaran.rekap.map(
                  (item: DataRekapPendaftaran, index: number) => (
                    <tr
                      key={index}
                      className="bg-white dark:bg-slate-950 hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-200 whitespace-nowrap">
                        {item.jalur_jenis}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                        {item.fakultas_nama}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                        {item.jurusan_nama}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400 font-medium">
                        {item.prodi_nama}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-700 dark:text-slate-300">
                        {item.daya_tampung}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-700 dark:text-slate-300">
                        {item.jumlah_pendaftar}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-700 dark:text-slate-300">
                        {item.jumlah_lulus}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-700 dark:text-slate-300">
                        {item.jumlah_isi_ukt}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-700 dark:text-slate-300">
                        {item.jumlah_valid_isi_ukt}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-700 dark:text-slate-300">
                        {item.jumlah_bayar_ukt}
                      </td>
                      <td className="px-4 py-3 text-center font-semibold text-blue-600 dark:text-blue-400">
                        {item.jumlah_daftar_kembali}
                      </td>
                    </tr>
                  ),
                )
              ) : (
                <tr>
                  <td
                    colSpan={10}
                    className="px-4 py-8 text-center text-slate-500 dark:text-slate-400"
                  >
                    Tidak ada data ditemukan.
                  </td>
                </tr>
              )}
            </tbody>
            {/* FOOTER TOTAL */}
            <tfoot className="bg-slate-100 dark:bg-slate-900 font-bold text-slate-900 dark:text-slate-100 sticky bottom-0 shadow-inner">
              <tr>
                <td
                  className="px-4 py-3 text-center uppercase text-xs tracking-wider"
                  colSpan={4}
                >
                  Total Keseluruhan
                </td>
                <td className="px-4 py-3 text-center">
                  {dataRekapPendaftaran?.total?.total_daya_tampung || 0}
                </td>
                <td className="px-4 py-3 text-center">
                  {dataRekapPendaftaran?.total?.total_pendaftar || 0}
                </td>
                <td className="px-4 py-3 text-center">
                  {dataRekapPendaftaran?.total?.total_lulus || 0}
                </td>
                <td className="px-4 py-3 text-center">
                  {dataRekapPendaftaran?.total?.total_isi_ukt || 0}
                </td>
                <td className="px-4 py-3 text-center">
                  {dataRekapPendaftaran?.total?.total_valid_isi_ukt || 0}
                </td>
                <td className="px-4 py-3 text-center">
                  {dataRekapPendaftaran?.total?.total_bayar_ukt || 0}
                </td>
                <td className="px-4 py-3 text-center text-blue-700 dark:text-blue-400">
                  {dataRekapPendaftaran?.total?.total_daftar_kembali || 0}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* MODAL FILTER */}
      <Modal
        show={showFilter}
        onClose={() => setShowFilter(false)}
        width="2xl"
        className="dark:bg-slate-950 dark:border-slate-800"
      >
        <FormFilter closeModal={() => setShowFilter(false)} />
      </Modal>
    </div>
  );
}
